import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { 
  ArrowLeftIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import api from '../../services/api'
import { recommendationsApi } from '../../services/api'
import Input from '../../components/common/Input'
import { toast } from 'react-hot-toast'

interface RecommendationForm {
  symbol: string
  company_name: string
  exchange: string
  recommendation_type: 'BUY' | 'SELL' | 'HOLD'
  entry_price: string
  target_price: string
  stop_loss: string
  target_1: string
  target_2: string
  target_3: string
  time_horizon: string
  risk_level: string
  title: string
  summary: string
  detailed_analysis: string
  is_premium: boolean
  status: 'draft' | 'published'
}

const CreateRecommendation: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditing = Boolean(id)
  const [loading, setLoading] = useState(false)
  const [fetching, setFetching] = useState(isEditing)
  const [formData, setFormData] = useState<RecommendationForm>({
    symbol: '',
    company_name: '',
    exchange: 'NSE',
    recommendation_type: 'BUY',
    entry_price: '',
    target_price: '',
    stop_loss: '',
    target_1: '',
    target_2: '',
    target_3: '',
    time_horizon: 'Medium-term',
    risk_level: 'Medium',
    title: '',
    summary: '',
    detailed_analysis: '',
    is_premium: false,
    status: 'draft'
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (isEditing && id) {
      const fetchRec = async () => {
        try {
          const rec = await recommendationsApi.getAdminRecommendation(Number(id))
          setFormData({
            symbol: rec.symbol,
            company_name: rec.company_name,
            exchange: (rec as any).exchange || 'NSE',
            recommendation_type: rec.recommendation_type as 'BUY' | 'SELL' | 'HOLD',
            entry_price: rec.entry_price?.toString() || '',
            target_price: rec.target_price?.toString() || '',
            stop_loss: rec.stop_loss?.toString() || '',
            target_1: (rec as any).target_1?.toString() || '',
            target_2: (rec as any).target_2?.toString() || '',
            target_3: (rec as any).target_3?.toString() || '',
            time_horizon: (rec as any).time_horizon || 'Medium-term',
            risk_level: (rec as any).risk_level || 'Medium',
            title: rec.title,
            summary: rec.summary || '',
            detailed_analysis: (rec as any).detailed_analysis || '',
            is_premium: rec.is_premium,
            status: (rec as any).status === 'published' ? 'published' : 'draft'
          })
        } catch (error) {
          console.error('Error fetching recommendation:', error)
          toast.error('Failed to fetch recommendation')
        } finally {
          setFetching(false)
        }
      }
      fetchRec()
    }
  }, [id, isEditing])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked
      setFormData(prev => ({ ...prev, [name]: checked }))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.symbol.trim()) newErrors.symbol = 'Symbol is required'
    if (!formData.company_name.trim()) newErrors.company_name = 'Company name is required'
    if (!formData.title.trim()) newErrors.title = 'Title is required'
    if (!formData.entry_price.trim()) newErrors.entry_price = 'Entry price is required'
    
    if (formData.entry_price && isNaN(Number(formData.entry_price))) {
      newErrors.entry_price = 'Entry price must be a valid number'
    }
    if (formData.target_price && isNaN(Number(formData.target_price))) {
      newErrors.target_price = 'Target price must be a valid number'
    }
    if (formData.stop_loss && isNaN(Number(formData.stop_loss))) {
      newErrors.stop_loss = 'Stop loss must be a valid number'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent, overrideStatus?: 'draft' | 'published') => {
    e.preventDefault()
    
    if (!validateForm()) return

    setLoading(true)
    
    try {
      const payload: any = { ...formData }
      if (overrideStatus) {
        payload.status = overrideStatus
      }
      const decimalFields = ['target_price', 'stop_loss', 'target_1', 'target_2', 'target_3']
      decimalFields.forEach(k => {
        if (payload[k] === '') payload[k] = null
      })
      
      if (isEditing && id) {
        await recommendationsApi.updateRecommendation(Number(id), payload)
        toast.success('Recommendation updated successfully')
      } else {
        await api.post('/recommendations', payload)
        toast.success('Recommendation created successfully')
      }
      
      navigate('/admin/content')
    } catch (error) {
      console.error('Error saving recommendation:', error)
      toast.error('Failed to save recommendation')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveAsDraft = async () => {
    const draftData = { ...formData, status: 'draft' as const }
    setFormData(draftData)
    handleSubmit({ preventDefault: () => {} } as React.FormEvent, 'draft')
  }

  const handlePublish = async () => {
    const publishData = { ...formData, status: 'published' as const }
    setFormData(publishData)
    handleSubmit({ preventDefault: () => {} } as React.FormEvent, 'published')
  }

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Loading recommendation...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/admin/content')}
          className="p-2"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-white">{isEditing ? 'Edit Recommendation' : 'Create Recommendation'}</h1>
          <p className="text-gray-400 mt-1">{isEditing ? 'Update existing stock recommendation' : 'Create a new stock recommendation'}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <Card>
              <h3 className="text-lg font-semibold text-white mb-4">Basic Information</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  label="Symbol *"
                  name="symbol"
                  value={formData.symbol}
                  onChange={handleInputChange}
                  error={errors.symbol}
                  placeholder="RELIANCE"
                  className="uppercase"
                />
                <Input
                  label="Company Name *"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleInputChange}
                  error={errors.company_name}
                  placeholder="Reliance Industries Ltd"
                />
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Exchange
                  </label>
                  <select
                    name="exchange"
                    value={formData.exchange}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-white text-gray-900 border border-dark-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="NSE">NSE</option>
                    <option value="BSE">BSE</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Recommendation Type *
                  </label>
                  <select
                    name="recommendation_type"
                    value={formData.recommendation_type}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-white text-gray-900 border border-dark-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="BUY">BUY</option>
                    <option value="SELL">SELL</option>
                    <option value="HOLD">HOLD</option>
                  </select>
                </div>
              </div>
            </Card>

            {/* Price Information */}
            <Card>
              <h3 className="text-lg font-semibold text-white mb-4">Price Information</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <Input
                  label="Entry Price *"
                  name="entry_price"
                  type="number"
                  step="0.01"
                  value={formData.entry_price}
                  onChange={handleInputChange}
                  error={errors.entry_price}
                  placeholder="2450.00"
                />
                <Input
                  label="Target Price"
                  name="target_price"
                  type="number"
                  step="0.01"
                  value={formData.target_price}
                  onChange={handleInputChange}
                  error={errors.target_price}
                  placeholder="2800.00"
                />
                <Input
                  label="Stop Loss"
                  name="stop_loss"
                  type="number"
                  step="0.01"
                  value={formData.stop_loss}
                  onChange={handleInputChange}
                  error={errors.stop_loss}
                  placeholder="2350.00"
                />
                <Input
                  label="Target 1"
                  name="target_1"
                  type="number"
                  step="0.01"
                  value={formData.target_1}
                  onChange={handleInputChange}
                  placeholder="2600.00"
                />
                <Input
                  label="Target 2"
                  name="target_2"
                  type="number"
                  step="0.01"
                  value={formData.target_2}
                  onChange={handleInputChange}
                  placeholder="2700.00"
                />
                <Input
                  label="Target 3"
                  name="target_3"
                  type="number"
                  step="0.01"
                  value={formData.target_3}
                  onChange={handleInputChange}
                  placeholder="2800.00"
                />
              </div>
            </Card>

            {/* Content */}
            <Card>
              <h3 className="text-lg font-semibold text-white mb-4">Content</h3>
              <div className="space-y-4">
                <Input
                  label="Title *"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  error={errors.title}
                  placeholder="Strong Q3 Results Expected"
                />
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Summary
                  </label>
                  <textarea
                    name="summary"
                    rows={3}
                    value={formData.summary}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-white text-gray-900 border border-dark-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Brief summary of the recommendation..."
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Detailed Analysis
                  </label>
                  <textarea
                    name="detailed_analysis"
                    rows={8}
                    value={formData.detailed_analysis}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-white text-gray-900 border border-dark-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Detailed technical and fundamental analysis..."
                  />
                </div>
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Publishing Options */}
            <Card>
              <h3 className="text-lg font-semibold text-white mb-4">Publishing</h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_premium"
                    checked={formData.is_premium}
                    onChange={handleInputChange}
                    className="rounded border-dark-600 text-blue-600 focus:ring-blue-500"
                  />
                  <label className="ml-2 text-sm text-gray-300">
                    Premium Content
                  </label>
                </div>
                
                <div className="space-y-3">
                  <Button 
                    type="button"
                    variant="outline" 
                    className="w-full"
                    onClick={handleSaveAsDraft}
                    disabled={loading}
                  >
                    Save as Draft
                  </Button>
                  <Button 
                    type="button"
                    className="w-full"
                    onClick={handlePublish}
                    disabled={loading}
                    loading={loading}
                  >
                    Publish Now
                  </Button>
                </div>
              </div>
            </Card>

            {/* Additional Settings */}
            <Card>
              <h3 className="text-lg font-semibold text-white mb-4">Additional Settings</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Time Horizon
                  </label>
                  <select
                    name="time_horizon"
                    value={formData.time_horizon}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-white text-gray-900 border border-dark-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Short-term">Short-term (1-3 months)</option>
                    <option value="Medium-term">Medium-term (3-12 months)</option>
                    <option value="Long-term">Long-term (1+ years)</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Risk Level
                  </label>
                  <select
                    name="risk_level"
                    value={formData.risk_level}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-white text-gray-900 border border-dark-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Low">Low Risk</option>
                    <option value="Medium">Medium Risk</option>
                    <option value="High">High Risk</option>
                  </select>
                </div>
              </div>
            </Card>

            {/* Help */}
            <Card>
              <div className="flex items-start space-x-3">
                <InformationCircleIcon className="w-5 h-5 text-blue-500 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-white">Tips</h4>
                  <ul className="text-xs text-gray-400 mt-2 space-y-1">
                    <li>• Use clear, concise titles</li>
                    <li>• Provide detailed technical analysis</li>
                    <li>• Include risk management levels</li>
                    <li>• Mark premium content appropriately</li>
                  </ul>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </form>
    </div>
  )
}

export default CreateRecommendation
